/**
 * Create or edit a custom view: name, data sources, the dimension column and
 * its rules, metric columns, and a live Data Preview. Leaving with unsaved
 * changes asks first (Discard Changes / Keep Editing), as AgencyAnalytics does.
 */

import { useState, type ReactNode } from 'react';

import type { CatalogueSource } from '../reports/reports-api.ts';
import { customApi, type CustomView, type ViewDefinition } from './custom-api.ts';
import { ViewColumns } from './ViewColumns.tsx';
import { ViewPreview } from './ViewPreview.tsx';
import { ViewSources } from './ViewSources.tsx';

export const BLANK_VIEW: ViewDefinition = {
  dimension: { name: 'Name', rules: [] },
  sources: [],
  columns: [],
};

export function ViewEditor(props: {
  organisationId: string;
  clientId: string;
  timezone: string;
  sources: CatalogueSource[];
  view: CustomView | null;
  start: { name: string; definition: ViewDefinition };
  onClose: () => void;
  onSaved: () => void;
}): ReactNode {
  const [name, setName] = useState(props.start.name);
  const [definition, setDefinition] = useState<ViewDefinition>(props.start.definition);
  const [dirty, setDirty] = useState(props.view === null);
  const [leaving, setLeaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const change = (next: ViewDefinition) => {
    setDefinition(next);
    setDirty(true);
  };
  const close = () => (dirty ? setLeaving(true) : props.onClose());
  const save = async () => {
    setBusy(true);
    setProblem(null);
    try {
      await customApi.saveView(
        props.organisationId,
        { business_unit_id: props.clientId, name, definition },
        props.view?.id,
      );
      props.onSaved();
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'The view could not be saved.');
      setBusy(false);
    }
  };
  return (
    <div className="rc-scrim">
      <div
        className="kp-modal cx-editor"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ve-title"
      >
        <header className="kp-modal-head">
          <h2 id="ve-title" className="sr-only">
            {props.view ? `Edit ${props.view.name}` : 'New view'}
          </h2>
          <label className="kp-title-field">
            <span className="sr-only">View name</span>
            <input
              value={name}
              placeholder="View name, e.g. Cross-channel ad performance"
              maxLength={120}
              onChange={(event) => {
                setName(event.target.value);
                setDirty(true);
              }}
            />
          </label>
          <button type="button" className="kp-close" aria-label="Close" onClick={close}>
            ×
          </button>
        </header>
        <div className="cx-editor-body">
          <ViewSources definition={definition} sources={props.sources} onChange={change} />
          <ViewColumns definition={definition} sources={props.sources} onChange={change} />
          <ViewPreview
            organisationId={props.organisationId}
            clientId={props.clientId}
            timezone={props.timezone}
            definition={definition}
          />
        </div>
        <footer className="kp-modal-foot">
          {problem && (
            <p className="kp-problem" role="alert">
              {problem}
            </p>
          )}
          <span className="ag-spacer" />
          <button type="button" className="btn secondary" onClick={close}>
            Cancel
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy || !name.trim() || !dirty}
            onClick={() => void save()}
          >
            {busy ? 'Saving…' : 'Save view'}
          </button>
        </footer>
        {leaving && (
          <div className="rc-scrim">
            <div
              className="rc kp-small"
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="ve-leave"
            >
              <h2 id="ve-leave">Unsaved Changes</h2>
              <p>This view has changes that are not saved.</p>
              <div className="kp-dialog-actions">
                <button type="button" className="btn secondary" onClick={props.onClose}>
                  Discard Changes
                </button>
                <button type="button" className="btn" onClick={() => setLeaving(false)} autoFocus>
                  Keep Editing
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
